"use client";

import { makeFunctionReference } from "convex/server";
import { useQuery } from "convex/react";
import { BarChart3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QuerySkeleton } from "@/components/ui/skeleton";
import type { CustomerPerformanceReportSnapshot } from "@/core/console/performance-reports";

interface PublicCustomerPerformanceReport {
  link: { token: string; customerId: string; expiresAt?: string; generatedAt?: string };
  report: CustomerPerformanceReportSnapshot;
}

const getPublicReport = makeFunctionReference<
  "query",
  { tenantId: string; token: string },
  PublicCustomerPerformanceReport | null
>("customerPerformanceReports:getPublic");

export function CustomerPerformanceReportView({ tenantId, token }: { tenantId: string; token: string }) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return <ReportPanel snapshot={demoReport(token)} />;
  }
  return <LiveCustomerPerformanceReportView tenantId={tenantId} token={token} />;
}

function LiveCustomerPerformanceReportView({ tenantId, token }: { tenantId: string; token: string }) {
  const snapshot = useQuery(getPublicReport, { tenantId, token });
  return <ReportPanel snapshot={snapshot} />;
}

function ReportPanel({ snapshot }: { snapshot: PublicCustomerPerformanceReport | null | undefined }) {
  if (snapshot === undefined) {
    return (
      <main className="mx-auto max-w-4xl px-6 py-8">
        <QuerySkeleton surface="customerReport" />
      </main>
    );
  }
  if (snapshot === null) {
    return <main className="mx-auto max-w-4xl px-6 py-8 text-sm text-muted-foreground">Report link unavailable.</main>;
  }

  const scorecard = snapshot.report.scorecard;
  return (
    <main className="mx-auto max-w-4xl space-y-4 px-6 py-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{snapshot.report.customerName}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Performance report</p>
        </div>
        <Badge variant="info">{scorecard.loadCount} loads</Badge>
      </div>

      <Card>
        <CardHeader className="p-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <BarChart3 size={15} />
            Scorecard
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 p-3 sm:grid-cols-2 lg:grid-cols-5">
          <Metric label="On-time" value={`${scorecard.onTimePct}%`} />
          <Metric label="Claim rate" value={`${scorecard.claimRatePct}%`} />
          <Metric label="Avg response" value={scorecard.avgResponseMinutes === null ? "n/a" : `${scorecard.avgResponseMinutes}m`} />
          <Metric label="Open exceptions" value={String(scorecard.openExceptionCount)} />
          <Metric label="Loads" value={String(scorecard.loadCount)} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="grid gap-3 p-3 text-sm sm:grid-cols-2">
          <div>
            <div className="text-xs uppercase text-muted-foreground">Generated</div>
            <div className="mt-1 font-medium">{formatDate(snapshot.report.generatedAt)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Expires</div>
            <div className="mt-1 font-medium">{snapshot.link.expiresAt ? formatDate(snapshot.link.expiresAt) : "No expiry"}</div>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function demoReport(token: string): PublicCustomerPerformanceReport {
  return {
    link: { token, customerId: "customer_demo", generatedAt: "2026-05-13T12:00:00.000Z" },
    report: {
      customerName: "Acme Foods",
      generatedAt: "2026-05-13T12:00:00.000Z",
      sections: ["summary", "on_time", "claims", "exceptions", "response_time"],
      scorecard: {
        loadCount: 24,
        onTimePct: 91.7,
        claimRatePct: 4.2,
        avgResponseMinutes: 34,
        openExceptionCount: 2,
      },
    },
  };
}

function formatDate(iso: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}
